import React, { useCallback, useEffect, useRef, useState } from 'react';
import { notification } from 'antd';
import { ClipboardText } from '@phosphor-icons/react';
import DashboardModal from '../DashboardModal';
import { useOneButtonStore } from '../../../../../store/oneButtonStore';
import { useCurrentUser } from '../../../../../contexts/UserContext';
import opportunityInfoServices from '../../../../../services/oneButtonServices/opportunityInfoService';
import { OBD_DASHBOARD_MODAL_Z } from '../oneButtonOverlayZ';
import { SalesRepChecklistEngine } from './salesrepChecklistEngine';
import { parseAuditTrailApiResponse, type ProcessedAuditTrailData } from './salesrepAuditTrail';
import { SalesRepChecklistPanel } from './SalesRepChecklistPanel';
import './SalesRepChecklist.css';

const EMPTY_AUDIT: ProcessedAuditTrailData = {
    groups: [],
    questionHistoryMap: new Map(),
    contributors: [],
    totalEntriesCount: 0,
    uniqueQuestionsCount: 0,
};

export const SalesRepChecklistModal: React.FC = () => {
    const theme = useOneButtonStore((s) => s.theme);
    const open = useOneButtonStore((s) => s.salesRepChecklistModalOpen);
    const setOpen = useOneButtonStore((s) => s.setSalesRepChecklistModalOpen);
    const setProgress = useOneButtonStore((s) => s.setSalesRepChecklistProgress);
    const opportunityID = useOneButtonStore((s) => s.opportunityID);
    const opportunityDetails = useOneButtonStore((s) => s.opportunityDetails);
    const salesForceId = useOneButtonStore((s) => s.salesForceId);

    const opportunityId =
        opportunityID ?? opportunityDetails?.Opportunity_Id ?? opportunityDetails?.OpportunityId ?? null;

    const { user } = useCurrentUser();

    const [tick, setTick] = useState(0);
    const engineRef = useRef<SalesRepChecklistEngine | null>(null);
    if (!engineRef.current || typeof engineRef.current.captureInitialResponses !== 'function') {
        engineRef.current = new SalesRepChecklistEngine(() => setTick((n) => n + 1));
    }
    const engine = engineRef.current;
    engine.userFullName = user?.user_name ?? '';

    const [loading, setLoading] = useState(false);
    const [auditTrail, setAuditTrail] = useState<ProcessedAuditTrailData>(EMPTY_AUDIT);
    const [noticeAPI, noticeContext] = notification.useNotification({ maxCount: 3 });

    const bumpProgress = useCallback(() => {
        const stats = engine.getOverallStats();
        setProgress(stats);
    }, [engine, setProgress]);

    const loadAuditTrail = useCallback(async (oppId: string | number) => {
        try {
            const data = await opportunityInfoServices.retrieveChecklistAuditTrail(oppId);
            setAuditTrail(parseAuditTrailApiResponse(Array.isArray(data) ? data : data?.data || []));
        } catch {
            setAuditTrail(EMPTY_AUDIT);
        }
    }, []);

    const loadChecklist = useCallback(
        async (viewState?: ReturnType<SalesRepChecklistEngine['captureChecklistViewState']>) => {
            if (!opportunityId) return;
            setLoading(true);
            engine.opportunityId = String(opportunityId);
            try {
                const [data] = await Promise.all([
                    opportunityInfoServices.retrieveSalesRepChecklistData(opportunityId),
                    loadAuditTrail(opportunityId),
                ]);
                engine.hydrateFromApi(data, viewState);
                bumpProgress();
                void opportunityInfoServices
                    .getChecklistResponseTone(opportunityId)
                    .then((toneData) => {
                        const payload = toneData?.data ?? toneData;
                        if (payload && typeof payload === 'object') {
                            useOneButtonStore.getState().setChecklistResponseTone(payload);
                        }
                    })
                    .catch(() => undefined);
            } catch (error) {
                console.error('Error retrieving checklist details:', error);
            } finally {
                setLoading(false);
            }
        },
        [bumpProgress, engine, loadAuditTrail, opportunityId],
    );

    useEffect(() => {
        if (!open || !opportunityId) return;
        void loadChecklist();
    }, [open, opportunityId, loadChecklist]);

    useEffect(() => {
        return () => {
            engineRef.current?.dispose();
        };
    }, []);

    const handleSave = async () => {
        if (!opportunityId) return;
        const viewState = engine.captureChecklistViewState();
        const { answers, allActiveQuestionIds, unsavedQuestions } = engine.buildSavePayload();
        if (!answers.length) return;
        setLoading(true);
        try {
            const data = await opportunityInfoServices.saveChecklistResponses({
                opportunityId,
                answers,
                allActiveQuestionIds,
            });
            if (data) {
                engine.markSaved(unsavedQuestions);
                void loadAuditTrail(opportunityId);
                void opportunityInfoServices
                    .getChecklistResponseTone(opportunityId)
                    .then((toneData) => {
                        const payload = toneData?.data ?? toneData;
                        if (payload && typeof payload === 'object') {
                            useOneButtonStore.getState().setChecklistResponseTone(payload);
                        }
                    })
                    .catch(() => undefined);
                noticeAPI.success({
                    message: data.message || 'Checklist saved',
                    placement: 'bottomLeft',
                    className: `obd-notification obd-theme-${theme}`,
                });
                await loadChecklist(viewState);
            }
        } catch (error) {
            console.error('Error saving checklist answers:', error);
            noticeAPI.error({
                message: 'Unable to save checklist',
                placement: 'bottomLeft',
                className: `obd-notification obd-theme-${theme}`,
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            {noticeContext}
            <DashboardModal
                open={open}
                onClose={() => {
                    if (
                        engine.unsavedChangesCount > 0 &&
                        typeof window !== 'undefined' &&
                        !window.confirm('You have unsaved checklist answers. Close anyway?')
                    ) {
                        return;
                    }
                    setOpen(false);
                }}
                title={
                    <>
                        <ClipboardText weight="duotone" aria-hidden />
                        <span>SalesRep Checklist</span>
                    </>
                }
                width="min(96vw, 1180px)"
                centered
                zIndex={OBD_DASHBOARD_MODAL_Z}
                theme={theme}
                loading={loading}
                loadingMessage="Loading checklist..."
                className={`obd-sync-modal obd-salesrep-checklist-modal obd-theme-${theme}`}
                rootClassName={`obd-sync-modal-root obd-salesrep-checklist-modal-root obd-theme-${theme}`}
                footer={null}
                destroyOnClose
            >
                <SalesRepChecklistPanel
                    engine={engine}
                    tick={tick}
                    auditTrail={auditTrail}
                    salesForceId={salesForceId}
                    onSave={handleSave}
                />
            </DashboardModal>
        </>
    );
};

export default SalesRepChecklistModal;
